import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { IcTheme, IcTopNavigation, IcNavigationItem } from "@ukic/react";

import { ToastProvider } from "./toast";
import { RoleProvider, useRole } from "./useRole";
import { Loading } from "./components/Loading";
import { ErrorState } from "./components/ErrorState";
import { Logo } from "./Logo";

import Fleet from "./pages/Fleet";
import Pending from "./pages/Pending";
import Device from "./pages/Device";
import Signals from "./pages/Signals";
import MapView from "./pages/Map";
import Entities from "./pages/Entities";
import EntityDetail from "./pages/EntityDetail";
import Survey from "./pages/Survey";
import Users from "./pages/Users";
import Live from "./pages/Live";
import Docs from "./pages/Docs";

// ICDS brand colour — the design system's primary navy (--ic-brand-blue-primary).
const BRAND = "#1b3c79";

interface NavLink {
  label: string;
  path: string;
  adminOnly?: boolean;
}

const NAV_LINKS: NavLink[] = [
  { label: "Fleet", path: "/fleet" },
  { label: "Signals", path: "/signals" },
  { label: "Map", path: "/map" },
  { label: "Entities", path: "/entities" },
  { label: "Survey", path: "/survey" },
  { label: "Pending", path: "/pending", adminOnly: true },
  { label: "Users", path: "/users", adminOnly: true },
  { label: "Docs", path: "/docs" },
];

function Shell() {
  const { loading, authError, isAdmin } = useRole();
  const navigate = useNavigate();
  const location = useLocation();

  const links = NAV_LINKS.filter((l) => !l.adminOnly || isAdmin);

  return (
    <div className="hl-shell">
      {/* Single (horizontal) top navigation — no side nav, no classification banner. */}
      <IcTopNavigation appTitle="HORIZONLOOK" status="" version="">
        <a
          slot="app-icon"
          aria-label="HORIZONLOOK home"
          href="/fleet"
          className="hl-logo-chip"
          onClick={(e) => {
            e.preventDefault();
            navigate("/fleet");
          }}
        >
          <Logo />
        </a>
        {links.map((l) => (
          <IcNavigationItem
            key={l.path}
            slot="navigation"
            label={l.label}
            href={l.path}
            selected={location.pathname.startsWith(l.path)}
            onClick={(e) => {
              e.preventDefault();
              navigate(l.path);
            }}
          />
        ))}
      </IcTopNavigation>

      <main className="hl-main">
        {loading ? (
          <Loading label="Authenticating…" />
        ) : authError ? (
          <ErrorState error={authError} />
        ) : (
          <Routes>
            <Route path="/" element={<Navigate to="/fleet" replace />} />
            <Route path="/fleet" element={<Fleet />} />
            <Route path="/signals" element={<Signals />} />
            <Route path="/map" element={<MapView />} />
            <Route path="/entities" element={<Entities />} />
            <Route path="/entity/:id" element={<EntityDetail />} />
            <Route path="/survey" element={<Survey />} />
            <Route path="/pending" element={<Pending />} />
            <Route path="/devices/:id" element={<Device />} />
            <Route path="/users" element={<Users />} />
            <Route path="/docs" element={<Docs />} />
            <Route path="/live/:slug/*" element={<Live />} />
            <Route path="*" element={<Navigate to="/fleet" replace />} />
          </Routes>
        )}
      </main>
    </div>
  );
}

export default function App() {
  return (
    <IcTheme brandColor={BRAND}>
      <RoleProvider>
        <ToastProvider>
          <Shell />
        </ToastProvider>
      </RoleProvider>
    </IcTheme>
  );
}
